if (!customElements.get('same-day-despatch')) {
  class SameDayDespatch extends HTMLElement {
    constructor() {
      super();
      this.timeZone = this.dataset.timeZone || 'Europe/London';
      this.cutoffHour = Number.parseInt(this.dataset.cutoffHour, 10) || 16;
      this.bankHolidays = new Set(
        (this.dataset.bankHolidays || '')
          .split(',')
          .map((date) => date.trim())
          .filter(Boolean)
      );
      this.updateTimer = null;
    }

    connectedCallback() {
      this.messageElement = this.querySelector('[data-despatch-message]');
      this.timingElement = this.querySelector('[data-despatch-timing]');
      window.clearInterval(this.updateTimer);
      this.update();
      this.updateTimer = window.setInterval(() => this.update(), 15000);
    }

    disconnectedCallback() {
      window.clearInterval(this.updateTimer);
    }

    getLondonParts(date) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(date);

      return Object.fromEntries(
        parts
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, Number.parseInt(part.value, 10)])
      );
    }

    getDateKey(dateParts) {
      return [
        dateParts.year,
        String(dateParts.month).padStart(2, '0'),
        String(dateParts.day).padStart(2, '0')
      ].join('-');
    }

    getCalendarDate(dateParts) {
      return new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day));
    }

    getDateParts(calendarDate) {
      return {
        year: calendarDate.getUTCFullYear(),
        month: calendarDate.getUTCMonth() + 1,
        day: calendarDate.getUTCDate()
      };
    }

    isWorkingDay(dateParts) {
      const calendarDate = this.getCalendarDate(dateParts);
      const dayOfWeek = calendarDate.getUTCDay();
      return dayOfWeek !== 0
        && dayOfWeek !== 6
        && !this.bankHolidays.has(this.getDateKey(dateParts));
    }

    getNextWorkingDay(dateParts) {
      const calendarDate = this.getCalendarDate(dateParts);

      do {
        calendarDate.setUTCDate(calendarDate.getUTCDate() + 1);
      } while (!this.isWorkingDay(this.getDateParts(calendarDate)));

      return this.getDateParts(calendarDate);
    }

    getTimeZoneOffset(timestamp) {
      const date = new Date(timestamp);
      const parts = this.getLondonParts(date);
      const representedAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
      );

      return representedAsUtc - Math.floor(timestamp / 1000) * 1000;
    }

    getCutoffTimestamp(dateParts) {
      const utcGuess = Date.UTC(
        dateParts.year,
        dateParts.month - 1,
        dateParts.day,
        this.cutoffHour,
        0,
        0
      );
      const firstOffset = this.getTimeZoneOffset(utcGuess);
      const firstResult = utcGuess - firstOffset;
      const correctedOffset = this.getTimeZoneOffset(firstResult);

      return utcGuess - correctedOffset;
    }

    formatRemaining(milliseconds) {
      const totalMinutes = Math.max(0, Math.ceil(milliseconds / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours === 0) {
        return `${minutes}m`;
      }

      if (minutes === 0) {
        return `${hours}h`;
      }

      return `${hours}h ${minutes}m`;
    }

    formatWorkingDay(dateParts) {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC',
        weekday: 'long'
      }).format(this.getCalendarDate(dateParts));
    }

    formatCutoff() {
      if (this.cutoffHour === 12) {
        return '12 noon';
      }

      if (this.cutoffHour < 12) {
        return `${this.cutoffHour}am`;
      }

      return `${this.cutoffHour - 12}pm`;
    }

    update() {
      if (!this.messageElement || !this.timingElement) {
        return;
      }

      const now = new Date();
      const nowParts = this.getLondonParts(now);
      const currentDate = {
        year: nowParts.year,
        month: nowParts.month,
        day: nowParts.day
      };
      const cutoffTimestamp = this.getCutoffTimestamp(currentDate);
      const beforeCutoff = now.getTime() < cutoffTimestamp;
      const cutoffLabel = this.formatCutoff();

      if (this.isWorkingDay(currentDate) && beforeCutoff) {
        this.dataset.state = 'counting';
        this.messageElement.textContent = `Order by ${cutoffLabel} for same-day despatch`;
        this.timingElement.textContent =
          `Order within ${this.formatRemaining(cutoffTimestamp - now.getTime())}`;
      } else {
        const nextWorkingDay = this.getNextWorkingDay(currentDate);
        this.dataset.state = 'next-day';
        this.messageElement.textContent =
          `Next despatch day: ${this.formatWorkingDay(nextWorkingDay)}`;
        this.timingElement.textContent =
          `Order by ${cutoffLabel} for despatch that day`;
      }

      this.timingElement.hidden = false;
    }
  }

  customElements.define('same-day-despatch', SameDayDespatch);
}
